import React, { useEffect, useRef, useState } from 'react';
import { DateRangePicker, Range, createStaticRanges } from 'react-date-range';
import {
  subDays,
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
  startOfQuarter,
  subQuarters,
  endOfQuarter,
  isSameDay,
} from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { RangeKeyDict } from 'react-date-range';

interface CustomDateRangePickerProps {
  onChange: (ranges: { startDate: Date; endDate: Date }) => void;
  months?: number;
}

const today = new Date();

const staticRanges = createStaticRanges([
  {
    label: 'Today',
    range: () => ({ startDate: today, endDate: today }),
  },
  {
    label: 'Yesterday',
    range: () => ({ startDate: subDays(today, 1), endDate: subDays(today, 1) }),
  },
  {
    label: 'Last 7 Days',
    range: () => ({ startDate: subDays(today, 6), endDate: today }),
  },
  {
    label: 'Last 14 Days',
    range: () => ({ startDate: subDays(today, 13), endDate: today }),
  },
  {
    label: 'Last 30 Days',
    range: () => ({ startDate: subDays(today, 29), endDate: today }),
  },
  {
    label: 'This Week',
    range: () => ({ startDate: startOfWeek(today, { weekStartsOn: 1 }), endDate: today }),
  },
  {
    label: 'Last Week',
    range: () => {
      const start = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
      return { startDate: start, endDate: endOfWeek(start, { weekStartsOn: 1 }) };
    },
  },
  {
    label: 'This Month',
    range: () => ({ startDate: startOfMonth(today), endDate: today }),
  },
  {
    label: 'Last Month',
    range: () => {
      const prev = subMonths(today, 1);
      return { startDate: startOfMonth(prev), endDate: endOfMonth(prev) };
    },
  },
  {
    label: 'This Quarter',
    range: () => ({ startDate: startOfQuarter(today), endDate: today }),
  },
  {
    label: 'Last Quarter',
    range: () => {
      const prev = subQuarters(today, 1);
      return { startDate: startOfQuarter(prev), endDate: endOfQuarter(prev) };
    },
  },
  {
    label: 'Last 3 Months',
    range: () => ({ startDate: subMonths(today, 3), endDate: today }),
  },
  {
    label: 'Last 6 Months',
    range: () => ({ startDate: subMonths(today, 6), endDate: today }),
  },
  {
    label: 'Year to Date',
    range: () => ({ startDate: startOfYear(today), endDate: today }),
  },
  {
    label: 'Last Year',
    range: () => {
      const prev = subYears(today, 1);
      return { startDate: startOfYear(prev), endDate: endOfYear(prev) };
    },
  },
  {
    label: 'Last 12 Months',
    range: () => ({ startDate: subMonths(today, 12), endDate: today }),
  },
]);

const inputRanges = [
  {
    label: 'Days up to today',
    range: (value: number) => ({ startDate: subDays(today, Math.max(value - 1, 0)), endDate: today }),
    getCurrentValue: (range: Range) => {
      if (!range.startDate || !range.endDate) return 0;
      if (!isSameDay(range.endDate, today)) return 0;
      return Math.round((today.getTime() - range.startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    },
  },
];

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({ onChange, months = 1 }) => {
  const [state, setState] = useState<Range[]>([
    {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (ranges: RangeKeyDict) => {
    const selection = ranges.selection;
    if (selection?.startDate && selection?.endDate) {
      setState([selection as Range]);
      onChange({
        startDate: selection.startDate,
        endDate: selection.endDate,
      });
    }
  };

  const formatDateRange = () => {
    const startDate = format(state[0].startDate!, 'MMM dd, yyyy');
    const endDate = format(state[0].endDate!, 'MMM dd, yyyy');
    return `${startDate} - ${endDate}`;
  };

  return (
    <div className='relative' ref={wrapperRef}>
      <div
        className='p-2 border rounded cursor-pointer pr-2 hover:bg-gray-50 text-sm flex'
        onClick={() => setIsOpen(!isOpen)}
      >
        {formatDateRange()}
        <svg
          className={`w-4 h-4 transition-transform duration-200 ml-auto ${isOpen ? 'transform rotate-180' : ''}`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </div>

      {isOpen && (
        <div className='absolute right-0 z-50 mt-2'>
          <div className='bg-white shadow-lg rounded-lg'>
            <DateRangePicker
              onChange={handleChange}
              moveRangeOnFirstSelection={false}
              months={months}
              ranges={state}
              direction='horizontal'
              maxDate={new Date()}
              staticRanges={staticRanges}
              inputRanges={inputRanges}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDateRangePicker;
